import { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { setForm } from '../redux/formReducer';
import {
  VehicleStatusCalculator,
  VehicleNameFormatter,
  VehicleDataEnricher,
  FormBuilder,
  LeadSourceDetector,
  AnalyticsTracker,
  Form,
  Promotion,
  FeatureAppDetails,
} from '../utils/analytics';
import {
  digitalLeadsFormLoad,
  digitalLeadsFormSubmissionSuccess,
  digitalLeadsFormSubmissionError,
} from '../utils/analyticsEventListeners';
import { FEATURE_APP_DETAILS } from '../constants';
import { OfferType } from '../types/analytics';

// ============================================================================
// CONFIGURATION
// ============================================================================

const IS_LOCAL_ENVIRONMENT = process.env.NODE_ENV === 'development';

// ============================================================================
// ANALYTICS LOGGER ADAPTER - Connects refactored code to existing system
// ============================================================================

const analyticsLogger = {
  logFormLoad: (form: Form, vehicle: any, featureApp: FeatureAppDetails, promotion?: Promotion) => {
    digitalLeadsFormLoad(form, vehicle, featureApp, promotion);
  },
  logFormSubmissionSuccess: (
    form: Form,
    vehicle: any,
    featureApp: FeatureAppDetails,
    promotion?: Promotion,
  ) => {
    digitalLeadsFormSubmissionSuccess(form, vehicle, featureApp, promotion);
  },
  logFormSubmissionError: (
    form: Form,
    vehicle: any,
    featureApp: FeatureAppDetails,
    promotion: Promotion | undefined,
    error: any,
  ) => {
    digitalLeadsFormSubmissionError(form, vehicle, featureApp, promotion, error);
  },
};

// ============================================================================
// HOOK 1: Generic Form Load
// ============================================================================

interface UseGenericFormLoadProps {
  isModalOpen: boolean;
  presetForm: string | null;
  vehicle?: any;
}
function useGenericFormLoad({ isModalOpen, presetForm, vehicle }: UseGenericFormLoadProps) {
  const dispatch = useDispatch();
  const tracker = useMemo(() => {
    return new AnalyticsTracker(analyticsLogger);
  }, []);
  const hasTracked = useRef(false);

  useEffect(() => {
    if (!isModalOpen || presetForm === null) {
      hasTracked.current = false;
      return;
    }

    const isGenericLead = LeadSourceDetector.isGenericLead(presetForm);
    const shouldTrack = isGenericLead && !vehicle && !hasTracked.current;

    if (shouldTrack) {
      const genericForm = FormBuilder.buildGenericForm({
        formName: '',
        formType: '',
        formData: {},
      } as Form);

      dispatch(setForm(genericForm));
      tracker.trackFormLoad(genericForm, undefined, FEATURE_APP_DETAILS);
      hasTracked.current = true;
    }
  }, [isModalOpen, vehicle, dispatch, tracker, presetForm]);
}

// ============================================================================
// HOOK 2: Form Submission Success
// ============================================================================

interface UseFormSubmissionSuccessProps {
  contactForm: Form;
  leadId: string;
  vehicle?: any;
  promotion?: Promotion;
}

export function useFormSubmissionSuccess({
  contactForm,
  leadId,
  vehicle,
  promotion,
}: UseFormSubmissionSuccessProps) {
  const tracker = useMemo(() => new AnalyticsTracker(analyticsLogger), []);
  const hasTracked = useRef(false);

  useEffect(() => {
    if (!leadId) {
      hasTracked.current = false;
      return;
    }

    if (leadId && !hasTracked.current) {
      const formWithLeadId = FormBuilder.addLeadId(contactForm, leadId);
      tracker.trackFormSubmissionSuccess(formWithLeadId, vehicle, FEATURE_APP_DETAILS, promotion);
      hasTracked.current = true;
    }
  }, [leadId, contactForm, vehicle, promotion, tracker]);
}

// ============================================================================
// HOOK 3: Form Submission Error
// ============================================================================

interface UseFormSubmissionErrorProps {
  error: boolean;
  errorMessage: { code: number; message: string };
  contactForm: Form;
  vehicle?: any;
  promotion?: Promotion;
}

export function useFormSubmissionError({
  error,
  errorMessage,
  contactForm,
  vehicle,
  promotion,
}: UseFormSubmissionErrorProps) {
  const tracker = useMemo(() => new AnalyticsTracker(analyticsLogger), []);
  const hasTracked = useRef(false);

  useEffect(() => {
    // Reset when error is cleared (modal closed)
    if (!error) {
      hasTracked.current = false;
      return;
    }

    if (error && errorMessage?.message?.length > 0 && !hasTracked.current) {
      const errorPayload = {
        error_message: errorMessage.message,
        error_code: errorMessage.code,
      };

      tracker.trackFormSubmissionError(
        contactForm,
        vehicle,
        FEATURE_APP_DETAILS,
        promotion,
        errorPayload,
      );
      hasTracked.current = true;
    }
  }, [error, errorMessage, contactForm, vehicle, promotion, tracker]);
}

// ============================================================================
// HOOK 4: Main Form Tracking (replaces useIxisFormTracking)
// ============================================================================

interface UseFormTrackingProps {
  isModalOpen: boolean;
  vehicle?: any;
  vehicleInView?: any;
  offers?: OfferType | null;
  dealerId: string | null;
  leadSource: string;
  contactForm: Form;
  promotion?: Promotion;
}

interface FormTrackingState {
  vehicleStatus?: string;
  vehicleSaleType?: string;
  vehicleSaleOrderType: string | null;
  orderStatusVehicle: string | null;
  vehicleWithEditedNames?: any;
  vehicleDataForPromotions?: any;
}

export function useFormTracking({
  isModalOpen,
  vehicle,
  vehicleInView,
  offers,
  dealerId,
  leadSource,
  contactForm,
  promotion,
}: UseFormTrackingProps): FormTrackingState {
  const dispatch = useDispatch();
  const tracker = useMemo(() => {
    return new AnalyticsTracker(analyticsLogger);
  }, []);

  const [state, setState] = useState<FormTrackingState>({
    vehicleStatus: undefined,
    vehicleSaleType: undefined,
    vehicleSaleOrderType: null,
    orderStatusVehicle: null,
    vehicleWithEditedNames: undefined,
    vehicleDataForPromotions: undefined,
  });

  const processedRef = useRef({
    vtpKey: '',
    promoKey: '',
  });

  useEffect(() => {
    if (!isModalOpen) return;

    const vehicleStatus = VehicleStatusCalculator.getVehicleStatus(vehicleInView);
    const vehicleSaleType = VehicleStatusCalculator.getSaleType(vehicleInView);
    const vehicleSaleOrderType = VehicleStatusCalculator.getSaleOrderType(vehicleInView);
    const orderStatusVehicle = VehicleStatusCalculator.getOrderStatus(vehicleInView);

    setState((prev) => ({
      ...prev,
      vehicleStatus,
      vehicleSaleType,
      vehicleSaleOrderType,
      orderStatusVehicle,
    }));

    const formType = LeadSourceDetector.getFormType(leadSource, !!vehicle);

    if (formType === 'vtp') {
      handleVTPForm();
    }
    if (formType === 'model') {
      handleNGCorMLPForm();
    }
    if (formType === 'promotions') {
      handlePromotionsForm();
    }

    // ========== VTP FORM HANDLER ==========
    function handleVTPForm() {
      if (!vehicle) return;

      const isAudi = VehicleNameFormatter.isAudiMake(vehicle);
      const enrichedVehicle = VehicleDataEnricher.enrichVehicle(vehicle, isAudi);
      const updatedForm = FormBuilder.buildVTPForm(contactForm, enrichedVehicle);

      // Use only stable vehicle identifiers for the key (exclude user-entered formData like visitorZipCode)
      const vtpKey = JSON.stringify({
        modelName: enrichedVehicle.titleText,
        orderType: vehicleSaleOrderType,
        orderStatus: orderStatusVehicle,
        dealerId: enrichedVehicle?.dealer?.id,
      });

      if (processedRef.current.vtpKey !== vtpKey) {
        setState((prev) => ({ ...prev, vehicleWithEditedNames: enrichedVehicle }));
        dispatch(setForm(updatedForm));
        tracker.trackFormLoad(updatedForm, enrichedVehicle, FEATURE_APP_DETAILS);
        processedRef.current.vtpKey = vtpKey;
      }
    }

    // ========== NGC/MLP FORM HANDLER ==========
    function handleNGCorMLPForm() {
      if (!vehicleInView) return;

      const enrichedVehicle = VehicleDataEnricher.enrichVehicle(vehicleInView, true);
      const updatedForm = FormBuilder.buildNGCorMLPForm(contactForm, enrichedVehicle, leadSource);

      // Use only stable vehicle identifiers for the key (exclude user-entered formData like visitorZipCode)
      const vtpKey = JSON.stringify({
        modelName: enrichedVehicle.titleText,
        orderType: vehicleSaleOrderType,
        orderStatus: orderStatusVehicle,
        leadSource,
      });

      if (processedRef.current.vtpKey !== vtpKey) {
        setState((prev) => ({ ...prev, vehicleWithEditedNames: enrichedVehicle }));
        dispatch(setForm(updatedForm));
        tracker.trackFormLoad(updatedForm, enrichedVehicle, FEATURE_APP_DETAILS);
        processedRef.current.vtpKey = vtpKey;
      }
    }

    // ========== PROMOTIONS FORM HANDLER ==========
    function handlePromotionsForm() {
      if (!offers) return;

      const promotionVehicle = VehicleDataEnricher.createPromotionVehicle(offers);
      const updatedForm = FormBuilder.buildPromotionsForm(
        contactForm,
        dealerId || '',
        offers.modelName || '',
      );

      // Use only stable promotion identifiers for the key (exclude user-entered formData like visitorZipCode)
      const promoKey = JSON.stringify({
        dealerId: dealerId || 'no-dealer',
        modelName: offers.modelName,
      });

      if (processedRef.current.promoKey !== promoKey) {
        setState((prev) => ({ ...prev, vehicleDataForPromotions: promotionVehicle }));
        dispatch(setForm(updatedForm));
        tracker.trackFormLoad(updatedForm, promotionVehicle, FEATURE_APP_DETAILS, promotion);
        processedRef.current.promoKey = promoKey;
      }
    }
  }, [
    isModalOpen,
    vehicle,
    vehicleInView,
    offers,
    dealerId,
    leadSource,
    contactForm,
    promotion,
    dispatch,
    tracker,
  ]);

  return state;
}

// ============================================================================
// EXPORT ALL HOOKS
// ============================================================================

export {
  useGenericFormLoad,
  useFormSubmissionSuccess as useSuccessfulFormSubmission,
  useFormSubmissionError as useUnsuccessfulFormError,
  useFormTracking as useIxisFormTracking,
};
